/**
 * FkReferenceEditor - Foreign key reference editor with async autocomplete
 *
 * Provides an autocomplete dropdown that fetches reference data from the
 * related table, allowing users to select from valid foreign key values.
 *
 * For combined link fields (multiple fields marked as is_link_field from the same
 * joined table), this editor uses the view's reference-options endpoint which
 * returns concatenated display values (e.g., "John Smith" instead of separate fields).
 */

import { useCallback, useState, useEffect, useRef, useContext } from "react";
import {
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import { GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { DirectCellUpdateContext } from "../DirectCellUpdateContext";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import { useWorkspaceContext } from "../../../../../providers/WorkspaceProvider";

// Simple debounce implementation
function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

interface FkReferenceEditorProps extends GridRenderEditCellParams {
  nullable?: boolean;
  fkReferenceTableId?: string;
  fkReferenceTableName?: string;
  fkDisplayColumn?: string;
  /** PK data type of the referenced table (uuid, string, number) */
  fkPkType?: "uuid" | "string" | "number";
  // Props for combined link fields
  viewId?: string;
  linkFieldIds?: string[];
  fkColumnKey?: string;
}

interface ReferenceOption {
  id: string;
  displayValue: string;
  secondaryInfo?: string;
}

export default function FkReferenceEditor({
  id,
  value,
  field,
  row,
  fkReferenceTableId,
  fkPkType,
  viewId,
  linkFieldIds,
  fkColumnKey,
}: FkReferenceEditorProps) {
  void fkReferenceTableId; // Used by direct FK fields

  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(true);
  const [options, setOptions] = useState<ReferenceOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState<ReferenceOption | null>(
    null,
  );

  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  // Use the reference-options endpoint for link fields
  const useViewEndpoint = !!(viewId && linkFieldIds && linkFieldIds.length > 0);
  const fieldId = linkFieldIds?.[0];

  // For combined link fields, get the actual FK value from the row data
  const fkValue =
    fkColumnKey && row ? (row[fkColumnKey] as string | undefined) : undefined;

  // Determine if value looks like a valid FK
  const isValidFkValue = (val: unknown): val is string => {
    if (typeof val !== "string" || !val) return false;
    if (fkPkType === "string") return true;
    if (fkPkType === "number") return /^\d+$/.test(val);
    return /^[0-9a-f-]{36}$/i.test(val); // UUID
  };

  const currentFkValue = fkValue || (isValidFkValue(value) ? value : undefined);

  // Fetch reference options
  const fetchOptions = useCallback(
    async (search: string) => {
      setLoading(true);
      try {
        if (useViewEndpoint && viewId && fieldId) {
          const params = new URLSearchParams({
            search,
            page: "1",
            page_size: "50",
          });
          if (currentFkValue) {
            params.set("current_value", currentFkValue);
          }

          const response = await authFetch(
            `/api/workspace/myvault/views/${viewId}/fields/${fieldId}/reference-options?${params}`,
            "fetch FK options",
            { headers: { "x-workspace-id": workspaceId } },
          );

          setOptions(response.options || []);
          return;
        }
        setOptions([]);
      } catch (error) {
        console.error("Error fetching FK reference options:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [useViewEndpoint, viewId, fieldId, currentFkValue, authFetch, workspaceId],
  );

  // Debounced search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((search: string) => fetchOptions(search), 300),
    [fetchOptions],
  );

  // Initial load
  useEffect(() => {
    fetchOptions("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [fetchOptions]);

  // Find current value in options
  useEffect(() => {
    if (value && options.length > 0) {
      const found = options.find((opt) => opt.id === value);
      if (found) {
        setSelectedOption(found);
      } else if (value && typeof value === "string") {
        setSelectedOption({ id: value, displayValue: value });
      }
    }
  }, [value, options]);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent | null, newInputValue: string) => {
      setInputValue(newInputValue);
      debouncedFetch(newInputValue);
    },
    [debouncedFetch],
  );

  const directCellUpdate = useContext(DirectCellUpdateContext);

  const handleChange = useCallback(
    (_event: React.SyntheticEvent | null, newValue: ReferenceOption | null) => {
      setSelectedOption(newValue);

      if (fkColumnKey) {
        // Update the FK column with the UUID
        const fkVal = newValue?.id || null;
        apiRef.current.setEditCellValue({
          id,
          field: fkColumnKey,
          value: fkVal,
        });

        // For new rows, also use directCellUpdate
        if (id === "__new_row__" && directCellUpdate) {
          directCellUpdate(id, fkColumnKey, fkVal).catch(console.error);
        }

        // Keep display field with display value
        const displayValue = newValue?.displayValue || null;
        apiRef.current.setEditCellValue({ id, field, value: displayValue });

        if (id === "__new_row__" && directCellUpdate) {
          directCellUpdate(id, field, displayValue).catch(console.error);
        }
      } else {
        // Simple FK field
        apiRef.current.setEditCellValue({
          id,
          field,
          value: newValue?.id || null,
        });

        if (id === "__new_row__" && directCellUpdate) {
          directCellUpdate(id, field, newValue?.id || null).catch(
            console.error,
          );
        }
      }
    },
    [apiRef, id, field, fkColumnKey, directCellUpdate],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    if (fkColumnKey && selectedOption) {
      apiRef.current.setEditCellValue({
        id,
        field,
        value: selectedOption.displayValue,
      });
    }
    setTimeout(() => {
      apiRef.current.stopCellEditMode({ id, field });
    }, 0);
  }, [apiRef, id, field, fkColumnKey, selectedOption]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        apiRef.current.stopCellEditMode({
          id,
          field,
          ignoreModifications: true,
        });
      }
    },
    [apiRef, id, field],
  );

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => handleClose()}
      value={selectedOption}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      loading={loading}
      getOptionLabel={(option) => option.displayValue}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      filterOptions={(x) => x} // Server-side filtering
      autoHighlight
      size="small"
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          onKeyDown={handleKeyDown}
          autoFocus
          placeholder="Search..."
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box>
            <Typography variant="body2">{option.displayValue}</Typography>
            {option.secondaryInfo && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {option.secondaryInfo}
              </Typography>
            )}
          </Box>
        </li>
      )}
    />
  );
}
